import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { LogoMark } from '../../components/LogoMark';
import { useAuth } from '../../contexts/AuthContext';
import { PwaInstallBanner } from '../../components/PwaInstallBanner';
import { checkBackendHealth } from '../../api/health';

export function LandingNav() {
  const { isAuthenticated } = useAuth();

  return (
    <header className="sticky top-0 z-50 border-b border-border-subtle-light dark:border-border-subtle-dark bg-surface-base-light/80 dark:bg-surface-base-dark/80 backdrop-blur-sm" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LogoMark className="h-7 w-auto" />
          <div className="flex items-center font-mono text-[12px] tracking-widest">
            <span className="text-primary-500 mr-1.5 select-none">&gt;</span>
            <span className="font-semibold uppercase text-text-primary-light dark:text-text-primary-dark">danny</span>
            <span className="hidden sm:inline text-text-tertiary-light dark:text-text-tertiary-dark select-none mx-1.5">/</span>
            <span className="hidden sm:inline font-normal uppercase text-text-tertiary-light dark:text-text-tertiary-dark">ai task management</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isAuthenticated ? (
            <Link
              to="/app"
              className="text-sm font-medium bg-primary-700 hover:bg-primary-800 text-surface-base-dark px-4 py-1.5 rounded-lg transition-colors"
            >
              Go to Dashboard
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors px-3 py-1.5"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="text-sm font-medium bg-primary-700 hover:bg-primary-800 text-surface-base-dark px-4 py-1.5 rounded-lg transition-colors"
              >
                Try Danny
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function LandingCTA({ headline, subtitle }: { headline: string; subtitle: string }) {
  return (
    <section className="max-w-5xl mx-auto px-6 pb-24">
      <div className="rounded-2xl bg-surface-raised-dark border border-border-subtle-dark px-8 py-12 text-center">
        <h2 className="text-3xl font-bold text-text-primary-dark mb-3 tracking-tight">
          {headline}
        </h2>
        <p className="text-text-secondary-dark mb-8 max-w-sm mx-auto">
          {subtitle}
        </p>
        <Link
          to="/register"
          className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-400 text-surface-base-dark font-semibold px-6 py-3 rounded-xl transition-colors"
        >
          Start for $2/month
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

export function LandingFooter() {
  return (
    <footer className="border-t border-border-subtle-light dark:border-border-subtle-dark">
      <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <LogoMark className="h-4 w-auto" />
            <span className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark">
              © {new Date().getFullYear()} Danny
            </span>
          </div>
          <span className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark font-mono opacity-60">
            Named after Daneel Olivaw
          </span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/privacy-policy" className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors">
            Privacy
          </Link>
          <Link to="/terms" className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors">
            Terms
          </Link>
          <a href="/security.html" className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors">
            Security
          </a>
          <Link to="/login" className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors">
            Sign in
          </Link>
        </div>
      </div>
    </footer>
  );
}

export function LandingPage({ children }: { children: ReactNode }) {
  // Preemptive API warm-up — fire a health check as soon as the landing
  // page loads so Cloud Run starts spinning up before the user clicks
  // "Sign in". By the time they reach the login page, the API is warm.
  useEffect(() => {
    checkBackendHealth().catch(() => {});
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-surface-base-light dark:bg-surface-base-dark">
      <LandingNav />
      {children}
      <LandingFooter />
      <PwaInstallBanner />
    </div>
  );
}
